export type ModelType = 'claude-sonnet-5' | 'claude-haiku-4-5';
export type EmbeddingsModelType = 'text-small';

export type Role = 'user' | 'model';
export type InputRole = Role | 'system';

export type ChatHistoryMessageItem = {
    /**
     * The role associated with the message.
     */
    role: InputRole;
    message: string;
};

type UserContentItem =
    | {
          type: 'message';
          message: string;
      }
    | {
          type: 'image';
          /**
           * Note that Anthropic models only supports `base64`.
           */
          sourceType: 'url' | 'base64';
          contentType: string;
          url: string;
      };

export type ChatHistoryUserContentItem = {
    role: 'user';
    content: UserContentItem[];
};

export type ChatHistoryItem = ChatHistoryMessageItem | ChatHistoryUserContentItem;

export type ChatHistory = ChatHistoryItem[];

export type Config = {
    /**
     * The temperature to use when generating completions.
     */
    temperature?: number;
    /**
     * The maximum number of tokens to generate. Thinking tokens count toward this limit.
     */
    maxTokens?: number;
    /**
     * Turns thinking off for models that otherwise decide for themselves when to think (Sonnet 5).
     * Use it for short, well-specified tasks where reasoning would only add latency and cost, or would
     * compete with the answer for a tight `maxTokens` budget.
     */
    disableThinking?: boolean;
};

export type FinishReason = 'stop' | 'maxTokens' | 'safety' | 'other';

export type ChatFunctionItem = {
    type: 'function';
    name: string;
    args: Record<string, unknown>;
};

export type ChatMessageItem = {
    type: 'message';
    message: string;
};

export type ChatItem = ChatFunctionItem | ChatMessageItem;

export type ChatCompletion = {
    chatItems: ChatItem[];
    role?: Role;
    index?: number;
    finishReason?: FinishReason;
};

export type ChatFunction = {
    name: string;
    description?: string;
    parameters: Record<string, unknown>;
};

export type ChatFunctionTool = {
    type: 'function';
    function: ChatFunction;
};

export type ChatTool = ChatFunctionTool;

export type ChatCompletions = ChatCompletion[];

export type ChatCompletionRequest = {
    model: ModelType;
    history?: ChatHistory;
    tools?: ChatTool[];
    /**
     * The tool choice to use when generating completions.
     */
    toolChoice?: {
        type: 'function';
        function: {
            name: string;
        };
    };
    config?: Config;
} & (
    | {
          message: string;
      }
    | {
          content: UserContentItem[];
      }
);

export type ChatCompletionResponse = {
    completions: ChatCompletions;
};

export type EmbeddingsRequest = {
    model: EmbeddingsModelType;
    messages: string[];
    // Not supported by all models, may be ignored
    dimensions?: number;
};

export type Vector = number[];

export type EmbeddingsResponse = {
    embeddings: Vector[];
};
